import { Injectable } from '@angular/core';

import { Idle } from '@ng-idle/core';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { tap, throttleTime } from 'rxjs/operators';

import { LoggingService } from '@logging/logging.service';
import { IdleUtils } from './idle-utils';
import { AbandonmentService } from '@auth-n/abandonment/abandonment.service';
import { environment } from '@env/environment';
import { createZonelessInterruptSources } from './zoneless-interrupt-sources';

@Injectable({ providedIn: 'root' })
export class IdleActivityService {
  private readonly timeoutSeconds = environment.idleActivityConfig.timeoutMinutes * 60;
  private logsSubscription: Subscription | undefined;
  private timedOutSubject = new BehaviorSubject<boolean>(false);
  idleTimeout$ = this.timedOutSubject.asObservable();

  constructor(
    private idle: Idle,
    private logger: LoggingService,
    private utils: IdleUtils,
    private abandonmentService: AbandonmentService
  ) {}

  initialise() {
    this.utils.trace('IDLE: service initialising');

    // buffer before watching
    this.idle.setIdle(3); // in seconds

    // countdown to timeout
    this.idle.setTimeout(this.timeoutSeconds); // seconds

    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.idle.setInterrupts(createZonelessInterruptSources());

    // Set up abandonment tracking on user activity, throttled to once per minute
    this.idle.onInterrupt.pipe(throttleTime(60 * 1000)).subscribe(() => {
      this.abandonmentService.setLastBecameActive();
    });

    // give our subscribers a timeout observable
    this.idle.onTimeout.subscribe(() => {
      this.logger.info('IDLE: timed-out');
      this.stopMonitoring();
      this.timedOutSubject.next(true);
    });

    this.setupDebugLogging();

    this.utils.trace('IDLE: service ready');
  }

  startMonitoring() {
    this.utils.trace('IDLE: start monitoring');
    this.timedOutSubject.next(false);
    this.idle.watch();
  }

  stopMonitoring() {
    this.utils.trace('IDLE: stop monitoring');
    this.idle.stop();
    if (!this.logsSubscription) return;
    (this.logsSubscription as Subscription).unsubscribe();
  }

  private setupDebugLogging() {
    let idledFor: number;
    const logInterval = 60 * 1000; // ms
    const log$ = timer(0, logInterval).pipe(
      tap((index) => {
        if (index === 0) idledFor = 0;
        const minutesIdle = idledFor === 0 ? 0 : Math.floor(idledFor / 1000 / 60);
        const minutesToTimeout = this.timeoutSeconds / 60 - minutesIdle;
        this.utils.trace(
          `IDLE Report: ${minutesIdle} minutes of inactivity have elapsed. Idle timeout will occur in ${minutesToTimeout} minutes.`
        );
        idledFor += logInterval;
      })
    );

    this.idle.onIdleStart.subscribe(() => {
      this.utils.trace('IDLE: went idle');
      this.logsSubscription = log$.subscribe();
    });
    this.idle.onIdleEnd.subscribe(() => {
      this.utils.trace('IDLE: became active');
      (this.logsSubscription as Subscription).unsubscribe();
    });
  }
}
